/**
 * Diário do Modo IA guardado no navegador: recarregar a aba (inclusive o recarregamento
 * automático depois de um deploy) não apaga o que a IA leu, decidiu e fez.
 *
 * Uma chave por usuário de IA: o localStorage é compartilhado entre as abas, e cada aba
 * pode ser de um usuário de IA diferente. O diário tem nome de paciente e o que ele pediu,
 * por isso vale só por {@link DIARY_TTL_MS} e é apagado ao sair. Sem armazenamento
 * disponível (janela anônima, bloqueio), o diário funciona como antes, só em memória.
 */

export interface StoredDiaryEntry {
    id: number
    at: number
    kind: string
    text: string
    chatId?: number
}

const KEY_PREFIX = 'ai-user:diary:'
/** Entradas mais antigas que isto são descartadas ao carregar. */
export const DIARY_TTL_MS = 24 * 60 * 60 * 1000

function keyFor(userId: number): string {
    return `${KEY_PREFIX}${userId}`
}

export function loadDiary(userId: number, limit: number, now = Date.now()): StoredDiaryEntry[] {
    try {
        const raw = window.localStorage.getItem(keyFor(userId))
        if (!raw) return []
        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []
        return parsed
            .filter(
                (entry): entry is StoredDiaryEntry =>
                    !!entry &&
                    typeof entry.id === 'number' &&
                    typeof entry.at === 'number' &&
                    typeof entry.text === 'string' &&
                    now - entry.at <= DIARY_TTL_MS,
            )
            .slice(0, limit)
    } catch {
        return []
    }
}

export function saveDiary(userId: number, entries: StoredDiaryEntry[]): void {
    try {
        window.localStorage.setItem(keyFor(userId), JSON.stringify(entries))
    } catch {
        // Cota cheia ou armazenamento bloqueado: o diário segue em memória.
    }
}

export function clearDiary(userId: number | null | undefined): void {
    if (userId == null) return
    try {
        window.localStorage.removeItem(keyFor(userId))
    } catch {
        // idem
    }
}
